import { useEffect, useRef, useState } from 'react';

interface VideoPlayerProps {
  cover: string;
  title: string;
  duration: string;
  rating: number;
}

type PlayerState = 'idle' | 'loading' | 'playing' | 'paused';

export default function VideoPlayer({ cover, title, duration, rating }: VideoPlayerProps) {
  const [state, setState] = useState<PlayerState>('idle');
  const [progress, setProgress] = useState(0);
  const timers = useRef<number[]>([]);

  useEffect(() => () => {
    timers.current.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    if (state !== 'playing') return undefined;
    const interval = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          window.clearInterval(interval);
          setState('idle');
          return 0;
        }
        return p + 0.6;
      });
    }, 200);
    return () => window.clearInterval(interval);
  }, [state]);

  const handlePlay = () => {
    if (state === 'loading') return;
    if (state === 'playing') {
      setState('paused');
      return;
    }
    if (state === 'paused') {
      setState('playing');
      return;
    }
    setState('loading');
    const t = window.setTimeout(() => {
      setState('playing');
    }, 1400);
    timers.current.push(t);
  };

  return (
    <div className="rounded-lg overflow-hidden border border-background-200 bg-secondary-950">
      <div className="relative w-full aspect-video">
        <img
          src={cover}
          alt={`${title}播放画面`}
          title={`${title} - 在线观看`}
          className={`w-full h-full object-cover object-top transition-all duration-700 ${
            state === 'playing' ? 'brightness-[0.55]' : 'brightness-[0.45]'
          }`}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/80 via-transparent to-secondary-950/30"></div>

        {state === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="w-12 h-12 rounded-full border-2 border-background-50/30 border-t-accent-400 animate-spin"></span>
            <span className="text-sm text-background-200">视频加载中，请稍候…</span>
          </div>
        )}

        {state !== 'loading' && (
          <button
            type="button"
            aria-label={state === 'playing' ? '暂停' : '播放'}
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center group cursor-pointer"
          >
            <span
              className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/95 group-hover:bg-primary-500 flex items-center justify-center transition-all duration-300 ${
                state === 'playing' ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
              }`}
            >
              <i className={`${state === 'playing' ? 'ri-pause-fill' : 'ri-play-fill'} text-background-50 text-3xl md:text-4xl`}></i>
            </span>
          </button>
        )}

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2 py-1 rounded-md bg-foreground-950/70 text-background-100 text-xs">
            演示播放器
          </span>
          {state === 'playing' && (
            <span className="px-2 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse"></span>
              模拟播放中
            </span>
          )}
        </div>

        <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-foreground-950/70 text-accent-300 text-xs font-semibold flex items-center gap-1">
          <i className="ri-star-fill text-accent-400"></i>
          {rating.toFixed(1)}
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-3 md:px-4 pb-3">
          <div className="h-1 w-full rounded-full bg-background-50/25 overflow-hidden">
            <div
              className="h-full rounded-full bg-accent-500 transition-all duration-200"
              style={{ width: `${state === 'idle' ? 0 : progress}%` }}
            ></div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-background-300">
            <span>{state === 'idle' ? '准备播放' : state === 'loading' ? '加载中…' : state === 'paused' ? '已暂停' : '播放中'}</span>
            <span>{duration}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 md:px-4 py-3 bg-secondary-900 text-background-300 text-xs md:text-sm">
        <i className="ri-information-line text-accent-400"></i>
        温馨提示：当前为前端模拟播放演示，暂未接入真实视频源，播放进度仅为展示效果。
      </div>
    </div>
  );
}